import React, { useState, useEffect, useCallback } from 'react';
import { MdDescription, MdFolder } from 'react-icons/md';
import { documentEditorApi } from '../../utils/api/DocumentEditorApi';
import { useClass } from '../../utils/ClassContext';

const NewDocumentModal = ({ isOpen, onClose, onConfirm, isCreating = false, title = null, defaultFileName = '' }) => {
  const { getClassContext } = useClass();
  const [fileName, setFileName] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState(null); // null means root level
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [error, setError] = useState(null);

  const loadSections = useCallback(async () => {
    setLoadingSections(true);
    try {
      const response = await documentEditorApi.getSections(getClassContext());
      if (response && response.success) {
        setSections(response.sections || []);
      } else {
        setError(response?.error || 'Failed to load sections');
      }
    } catch (err) {
      console.error('Error loading sections:', err);
      setError('Failed to load sections');
    } finally {
      setLoadingSections(false);
    }
  }, [getClassContext]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens, using defaultFileName if provided
      setFileName(defaultFileName || '');
      setSelectedSectionId(null);
      setError(null);
      loadSections();
    }
  }, [isOpen, defaultFileName, loadSections]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (fileName.trim()) {
      onConfirm({
        name: fileName.trim(),
        sectionId: selectedSectionId
      });
      // Don't close modal here - parent will handle it after success
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setFileName(defaultFileName || '');
      setSelectedSectionId(null);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-full">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2">
          <MdDescription size={20} />
          <span>{title || 'Create New Document'}</span>
        </h3>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Name
            </label>
            <input
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter document name..."
              autoFocus
              disabled={isCreating}
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section
            </label>
            {loadingSections ? (
              <div className="flex items-center justify-center py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-sm text-gray-500">Loading sections...</span>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Root level option */}
                <label className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="section"
                    value=""
                    checked={selectedSectionId === null}
                    onChange={() => setSelectedSectionId(null)}
                    disabled={isCreating}
                    className="text-blue-600"
                  />
                  <MdDescription size={16} className="text-blue-600" />
                  <span className="text-sm text-gray-700">Root Level (No Section)</span>
                </label>
                
                {/* Section options */}
                {sections.map(section => (
                  <label 
                    key={section.section_id} 
                    className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="section"
                      value={section.section_id}
                      checked={selectedSectionId === section.section_id}
                      onChange={() => setSelectedSectionId(section.section_id)}
                      disabled={isCreating}
                      className="text-blue-600"
                    />
                    <MdFolder size={16} className="text-yellow-600" />
                    <span className="text-sm text-gray-700">{section.name}</span>
                    {section.description && (
                      <span className="text-xs text-gray-500">- {section.description}</span>
                    )}
                  </label>
                ))}
                
                {sections.length === 0 && !loadingSections && (
                  <div className="text-sm text-gray-500 italic p-2 text-center">
                    No sections available. Document will be created at root level.
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!fileName.trim() || isCreating}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isCreating && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isCreating ? 'Creating...' : 'Create Document'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewDocumentModal;